import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import z from 'zod';
import { Input } from '../../../components/ui/Input';
import { Button } from '../../../components/ui/Button';
import { Spinner } from '../../../components/ui/Spinner';
import { walletService } from '../../../services/wallet';
import { useSearchParams } from 'react-router-dom';

const QUICK_AMOUNTS = [500, 1000, 2000, 5000];

interface CustomerDetails {
  crn: string;
  firstName: string;
  lastName: string;
  fullName: string;
  companyName: string;
  balance: number;
  b2bBalance?: number;
}

const remarkSchema = z
  .string()
  .trim()
  .min(3, 'Remark must be at least 3 characters long')
  .max(100, 'Remark cannot exceed 100 characters')
  .refine(
    (val) => {
      if (!val) return true;
      const hasLetter = /[a-zA-Z]/.test(val);
      const isAlphaNumeric = /^[a-zA-Z0-9\s.,\-\/()]+$/.test(val);
      return hasLetter && isAlphaNumeric;
    },
    {
      message: 'Remark must contain at least one letter and can only contain alphanumeric characters, spaces, and basic punctuation (.,-/)',
    }
  );

export const AdminWalletTransactionPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const initialCrn = searchParams.get('crn') || '';

  const [crn, setCrn] = useState(initialCrn);
  const [loadingUser, setLoadingUser] = useState(false);
  const [customer, setCustomer] = useState<CustomerDetails | null>(null);

  const [amount, setAmount] = useState<number | ''>('');
  const [transactionType, setTransactionType] = useState<'credit' | 'debit'>('credit');
  const [walletTarget, setWalletTarget] = useState<'b2c' | 'b2b'>(window.location.pathname.includes('/b2b') ? 'b2b' : 'b2c');
  const [paymentRef, setPaymentRef] = useState('');
  const [remark, setRemark] = useState('');
  const [remarkError, setRemarkError] = useState<string | null>(null);

  const [showConfirm, setShowConfirm] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [successState, setSuccessState] = useState(false);

  useEffect(() => {
    if (initialCrn) {
      setCrn(initialCrn);
      fetchUserDetails(initialCrn);
    }
  }, [initialCrn]);

  const fetchUserDetails = async (searchCrn: string) => {
    if (!searchCrn.trim()) {
      toast.error('Please enter a CRN number');
      return;
    }
    setLoadingUser(true);
    setCustomer(null);
    try {
      const data = await walletService.getAdminUserDetailsByCrn(searchCrn.trim());
      if (data && data.success) {
        setCustomer({
          crn: data.crn,
          firstName: data.firstName,
          lastName: data.lastName,
          fullName: data.fullName,
          companyName: data.companyName,
          balance: data.balance,
          b2bBalance: data.b2bBalance,
        });
        toast.success('Customer found');
      } else {
        toast.error(data?.message || 'Customer not found');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Error searching for customer CRN');
    } finally {
      setLoadingUser(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchUserDetails(crn);
  };

  const validateForm = (): boolean => {
    if (!customer) {
      toast.error('Please search and select a customer first');
      return false;
    }

    if (!amount || Number(amount) <= 0) {
      toast.error('Please enter a valid amount greater than 0');
      return false;
    }

    if (!paymentRef.trim()) {
      toast.error('Please enter a payment reference number');
      return false;
    }

    const result = remarkSchema.safeParse(remark);
    if (!result.success) {
      setRemarkError(result.error.issues[0].message);
      toast.error(result.error.issues[0].message);
      return false;
    }

    setRemarkError(null);
    return true;
  };

  const handleSubmitClick = () => {
    if (validateForm()) {
      setShowConfirm(true);
    }
  };

  const handleConfirmSubmit = async () => {
    if (!customer || !amount) return;
    setProcessing(true);
    try {
      const response = await walletService.postAdminWalletTransaction({
        crn: customer.crn,
        payment_reference_number: paymentRef.trim(),
        amount: Number(amount),
        transaction_type: transactionType,
        remark: remark.trim(),
        walletType: walletTarget,
      });

      if (response && response.success) {
        setSuccessState(true);
        toast.success(response.message || 'Transaction processed successfully!');
      } else {
        toast.error(response?.message || 'Transaction failed');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to process transaction');
    } finally {
      setProcessing(false);
      setShowConfirm(false);
    }
  };

  const resetForm = () => {
    setAmount('');
    setPaymentRef('');
    setRemark('');
    setRemarkError(null);
    setSuccessState(false);
    setShowConfirm(false);
    if (!initialCrn) {
      setCrn('');
      setCustomer(null);
    } else {
      fetchUserDetails(initialCrn);
    }
  };

  return (
    <div className="py-2 lg:py-4">
      {/* Header */}
      <div className="space-y-2 mb-8">
        <h1 className="text-2xl lg:text-3xl font-bold text-text2"> Wallet Transaction</h1>
        <p className="text-sm text-text3">Manual wallet adjustments for registered users</p>
      </div>

      {!successState ? (
        <div className="bg-card rounded-[16px] shadow-sm border p-6">
          <div className="flex flex-col gap-8">
            {/* Title Section */}
            <div className="border-b pb-4">
              <h2 className="text-xl font-semibold mb-1">Wallet Transaction</h2>
              <p className="text-sm text-text3">Search active users by CRN and create credit or debit transactions</p>
            </div>

            {/* Form layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              {/* Left Column: Search & Customer display */}
              <div className="space-y-6">
                <div className="flex flex-col gap-2">
                  <label className="font-semibold text-text2">Customer Search</label>
                  <form onSubmit={handleSearch} className="flex gap-2 items-start">
                    <Input
                      required
                      value={crn}
                      onChange={(e) => setCrn(e.target.value)}
                      placeholder="Enter Customer CRN (e.g. PP0012)"
                      disabled={loadingUser || !!initialCrn}
                      className="flex-1"
                    />
                    {!initialCrn && (
                      <Button
                        type="submit"
                        disabled={loadingUser}
                        className="h-10 px-6 bg-accent hover:bg-accent/90"
                      >
                        {loadingUser ? <Spinner size="sm" /> : 'Search'}
                      </Button>
                    )}
                  </form>
                </div>

                {customer && (
                  <div className="bg-background border rounded-xl p-5 space-y-4 animate-fade-in">
                    <div className="flex gap-4 items-center">
                      <div className="w-12 h-12 bg-accent text-white rounded-full flex items-center justify-center font-bold text-lg shadow-sm">
                        {customer.firstName?.[0] || 'U'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-text2 truncate">{customer.fullName}</p>
                        <p className="text-xs text-text3 truncate">{customer.companyName || 'No Company Name'}</p>
                        <span className="inline-block font-mono bg-white px-2 py-0.5 mt-1 rounded border border-muted text-xs text-text">{customer.crn}</span>
                      </div>
                    </div>

                    <div className="border-t pt-4 space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-semibold text-text3 uppercase">B2C Balance</span>
                        <span className="text-lg font-bold text-accent">₹{customer.balance?.toFixed(2) || '0.00'}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-semibold text-text3 uppercase">B2B Balance</span>
                        <span className="text-lg font-bold text-accent">₹{(customer as any).b2bBalance?.toFixed(2) || '0.00'}</span>
                      </div>
                    </div>
                  </div>
                )}

                {!customer && !loadingUser && (
                  <div className="bg-background border border-dashed border-muted rounded-xl p-8 text-center text-text3 text-sm">
                    Enter customer CRN code and click Search to pull up user profile.
                  </div>
                )}
              </div>

              {/* Right Column: Adjustment Parameters */}
              <div className="space-y-6">
                {!customer ? (
                  <div className="h-full flex items-center justify-center py-12 border border-dashed border-muted rounded-xl text-text3 text-sm">
                    Search and verify customer to activate transaction controls.
                  </div>
                ) : (
                  <div className="space-y-6">


                    {/* Transaction Mode Selector */}
                    <div className="flex flex-col gap-2">
                      <label className="font-semibold text-text2">Transaction Mode</label>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => setTransactionType('credit')}
                          className={`px-6 py-2.5 text-sm font-semibold rounded-md border flex-1 transition-all ${transactionType === 'credit'
                            ? 'border-accent bg-accent text-white'
                            : 'border-muted text-text3 hover:border-gray-400 bg-white'
                            }`}
                        >
                          Credit (+)
                        </button>
                        <button
                          type="button"
                          onClick={() => setTransactionType('debit')}
                          className={`px-6 py-2.5 text-sm font-semibold rounded-md border flex-1 transition-all ${transactionType === 'debit'
                            ? 'border-destructive bg-destructive text-white'
                            : 'border-muted text-text3 hover:border-gray-400 bg-white'
                            }`}
                        >
                          Debit (-)
                        </button>
                      </div>
                    </div>

                    {/* Amount & Quick Chips */}
                    <div className="space-y-2">
                      <Input
                        required
                        type="number"
                        label="Amount"
                        value={amount}
                        onChange={(e) => {
                          const val = e.target.value;
                          setAmount(val === '' ? '' : Number(val));
                        }}
                        placeholder="Enter adjustment amount"
                        startEnhancer={<span className="text-text3 font-semibold">₹</span>}
                      />
                      <div className="flex justify-between gap-3 pt-1">
                        {QUICK_AMOUNTS.map((amt) => (
                          <button
                            key={amt}
                            type="button"
                            onClick={() => setAmount(amt)}
                            className={`flex-1 py-2 rounded-lg text-xs font-semibold border transition-all ${amount === amt
                              ? 'border-accent bg-accent/10 text-accent font-bold'
                              : 'border-muted hover:border-gray-400 text-text3 bg-white'
                              }`}
                          >
                            + ₹{amt}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Reference Number */}
                    <Input
                      required
                      label="Payment Reference Number"
                      value={paymentRef}
                      onChange={(e) => setPaymentRef(e.target.value)}
                      placeholder="Bank reference or internal ticket ID"
                    />

                    {/* Remark */}
                    <Input
                      label="Remark / Description"
                      value={remark}
                      onChange={(e) => {
                        const value = e.target.value;
                        setRemark(value);
                        if (value.trim()) {
                          const result = remarkSchema.safeParse(value);
                          setRemarkError(result.success ? null : result.error.issues[0].message);
                        } else {
                          setRemarkError(null);
                        }
                      }}
                      placeholder="Reason for adjustment (e.g. Compensation, Recharge)"
                      error={remarkError || undefined}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Actions at the bottom */}
            {customer && (
              <div className="flex justify-center gap-4 border-t pt-6">
                <Button
                  variant="outline"
                  className="px-10 py-2.5 border-muted text-text3 hover:bg-gray-200"
                  onClick={resetForm}
                >
                  Reset Form
                </Button>
                <Button
                  variant={transactionType === 'credit' ? 'default' : 'destructive'}
                  className="px-10 py-2.5"
                  onClick={handleSubmitClick}
                >
                  Post Transaction
                </Button>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Success Screen */
        <div className="bg-card rounded-[16px] shadow-sm border p-8 max-w-xl mx-auto text-center flex flex-col items-center justify-center space-y-6 animate-scale-up">
          <div className="w-16 h-16 bg-green-50 text-accent rounded-full border border-accent/20 flex items-center justify-center shadow-sm">
            <svg className="w-8 h-8 animate-bounce" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          </div>

          <div>
            <h3 className="text-xl font-bold text-text2">Wallet Entry Successful!</h3>
            <p className="text-sm text-text3 mt-1">Wallet balance and transaction log updated</p>
          </div>

          <div className="w-full bg-background border rounded-xl p-5 text-left space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-text3">Customer:</span>
              <span className="font-semibold text-text2">{customer?.fullName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-text3">CRN Number:</span>
              <span className="font-mono font-semibold text-text2">{customer?.crn}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-text3">Adjustment:</span>
              <span className={`font-bold uppercase ${transactionType === 'credit' ? 'text-accent' : 'text-destructive'}`}>
                {transactionType === 'credit' ? 'CREDIT' : 'DEBIT'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-text3">Amount:</span>
              <span className="font-bold text-text2">₹{Number(amount).toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-text3">Reference:</span>
              <span className="font-mono text-text2">{paymentRef}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-text3">Remark:</span>
              <span className="text-text2 truncate max-w-[250px]">{remark || 'N/A'}</span>
            </div>
          </div>

          <Button
            className="w-full bg-accent hover:bg-accent/90 text-white"
            onClick={resetForm}
          >
            Create New Transaction
          </Button>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirm && customer && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-card rounded-[16px] w-full max-w-md p-6 shadow-xl border flex flex-col space-y-5 animate-scale-up">
            <div className="text-center space-y-1">
              <div className="w-12 h-12 bg-amber-50 text-amber-600 rounded-full flex items-center justify-center mx-auto mb-2 border border-amber-200">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h4 className="text-lg font-bold text-text2">Confirm Wallet Entry</h4>
              <p className="text-xs text-text3">Please double-check details before posting to database.</p>
            </div>

            <div className="bg-background rounded-xl p-4 space-y-3 text-xs">
              <div className="flex justify-between border-b border-muted pb-2">
                <span className="text-text3 uppercase">Customer CRN</span>
                <span className="font-bold text-text2">{customer.crn}</span>
              </div>
              <div className="flex justify-between border-b border-muted pb-2">
                <span className="text-text3 uppercase">Customer Name</span>
                <span className="font-bold text-text2 truncate max-w-[180px]">{customer.fullName}</span>
              </div>
              <div className="flex justify-between border-b border-muted pb-2">
                <span className="text-text3 uppercase">Target Wallet</span>
                <span className={`font-bold uppercase tracking-wider`}>
                  {walletTarget === 'b2c' ? 'B2C WALLET' : 'B2B WALLET'}
                </span>
              </div>
              <div className="flex justify-between border-b border-muted pb-2">
                <span className="text-text3 uppercase">Adjustment Type</span>
                <span className={`font-bold uppercase tracking-wider ${transactionType === 'credit' ? 'text-accent' : 'text-destructive'}`}>
                  {transactionType === 'credit' ? 'CREDIT (+)' : 'DEBIT (-)'}
                </span>
              </div>
              <div className="flex justify-between border-b border-muted pb-2">
                <span className="text-text3 uppercase">Transaction Amount</span>
                <span className="font-bold text-text2 text-sm">₹{Number(amount).toFixed(2)}</span>
              </div>
              <div className="flex justify-between border-b border-muted pb-2">
                <span className="text-text3 uppercase">Payment Reference</span>
                <span className="font-mono font-semibold text-text">{paymentRef}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-text3 uppercase">Remark / Description</span>
                <span className="text-text bg-white p-2 rounded border border-muted break-words">{remark || 'No remark provided'}</span>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowConfirm(false)}
                disabled={processing}
                className="flex-1 border-muted text-text3"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmSubmit}
                disabled={processing}
                variant={transactionType === 'credit' ? 'default' : 'destructive'}
                className="flex-1"
              >
                {processing ? <Spinner size="sm" /> : 'Confirm & Post'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminWalletTransactionPage;
